import { Fragment, useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HiddenLabel, Select, type SelectPosition } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { Typography } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { isOptionLike } from '../BaseSelect/selectUtils';
import type { BaseSingleSelectProps, Option } from '../BaseSelect/BaseSelect';

/**
 * Flag-on (`fpl-migration`) implementation of the fixed-list, non-searchable `Dropdown`, backed by
 * the high-level FPL `Select` compound component.
 *
 * Kept private to the `Dropdown` wrapper — it is never exported from the UI barrel. Searchable
 * (`search`) dropdowns are intentionally NOT handled here; `Dropdown` keeps rendering the legacy
 * `BaseSelect` path for those until the separate AutocompletePrimitive migration.
 *
 * Deliberately dropped vs. the legacy trigger (product accepted these as small visual diffs):
 * - `matchTriggerWidth` / exact px trigger width — an owner wrapper carries the caller `width` and the
 *   trigger fills it; the FPL menu already min-widths to the trigger.
 * - custom `offset`, `noBorder`, caret chrome — owned by FPL.
 * - `sx` / `menuSx` / `optionSx` (MUI) — legacy-only, ignored here.
 */

const getOptionKey = <T,>(option: Option<T>): string =>
  typeof option.id === 'function' ? option.id(option.value) : String(option.id);

const isPrimitiveLabel = (label: ReactNode): boolean =>
  typeof label === 'string' || typeof label === 'number' || typeof label === 'boolean';

export type DropdownFplPosition = SelectPosition;

export interface DropdownFplProps<T, Opt extends Option<T>> extends Pick<
  BaseSingleSelectProps<T, Opt>,
  'options' | 'onChange' | 'onOpen' | 'onClose' | 'onHighlightedOptionChange'
> {
  value: Opt | T | null;
  confirmedValue?: Opt | T | null;
  disabled?: boolean;
  /** Resolved FPL trigger size (`md` = 24px, `lg` = 32px). Mapped by `Dropdown` from `sx.height`/`size`. */
  size?: 'md' | 'lg';
  width?: string;
  placeholder?: string;
  emptyState?: ReactNode;
  noOptionsText?: ReactNode;
  id?: string;
  position?: DropdownFplPosition;
}

export const DropdownFpl = <T, Opt extends Option<T>>({
  options,
  onChange,
  onOpen,
  onClose,
  onHighlightedOptionChange,
  size = 'md',
  value,
  confirmedValue,
  disabled,
  width = '100px',
  placeholder,
  emptyState,
  noOptionsText,
  id,
  position,
}: DropdownFplProps<T, Opt>) => {
  const { t } = useTranslation();
  // FPL Select is keyed by a string DataType = the option's stable `id`, NOT its `value`. This mirrors
  // the legacy identity (`useSelect.getOptionId` / `getIsSelected` compare ids) and is required for
  // correctness when option values are non-unique (e.g. NumberSelectorNode builds `{ id: index,
  // value: parseFloat(...) }` where two rows can share a value): selecting a row must report that
  // exact row's option (and thus its id), which value-based matching cannot do.
  const findByKey = useCallback(
    (key: string | undefined) => (key === undefined ? undefined : options.find((o) => getOptionKey(o) === key)),
    [options],
  );

  const selectedKey = useMemo<string | undefined>(() => {
    const rawValue = confirmedValue ?? value;
    if (rawValue === null || rawValue === undefined) return undefined;
    if (isOptionLike(rawValue)) return getOptionKey(rawValue);
    // Raw value: resolve to an option by value (first match), mirroring legacy `BaseSelect` (which
    // does `options.find(o => o.value === value)` for selection highlight/label).
    const match = options.find((o) => o.value === rawValue);
    return match ? getOptionKey(match) : undefined;
  }, [confirmedValue, value, options]);

  const selectedOption = useMemo(() => findByKey(selectedKey), [findByKey, selectedKey]);

  const handleChange = useCallback(
    (nextKey: string | undefined) => {
      const option = findByKey(nextKey);
      if (option) onChange(option);
    },
    [findByKey, onChange],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) onOpen?.();
      else onClose?.();
    },
    [onOpen, onClose],
  );

  const handleSelectionChange = useMemo(() => {
    if (!onHighlightedOptionChange) return undefined;
    return (nextKey?: string) => {
      onHighlightedOptionChange(findByKey(nextKey) ?? null);
    };
  }, [onHighlightedOptionChange, findByKey]);

  const stringPlaceholder = placeholder ?? (typeof emptyState === 'string' ? emptyState : undefined);

  // The trigger shows the selected option's text automatically, so it is only overridden when the
  // label is a custom ReactNode — or when nothing is selected, where FPL cannot be relied on. It keeps
  // the last selected option's label in its own state, never clears it, and renders it ahead of the
  // placeholder, so a field whose value returns to null keeps showing the option it no longer has:
  // unlocking the crop node's aspect ratio left the old ratio in place instead of "Custom". Rendering
  // the empty state ourselves takes precedence over that stale label.
  const triggerChildren = useMemo<ReactNode>(() => {
    if (selectedOption) return isPrimitiveLabel(selectedOption.label) ? undefined : selectedOption.label;
    return emptyState ?? stringPlaceholder ?? undefined;
  }, [selectedOption, emptyState, stringPlaceholder]);
  const accessibleLabel = stringPlaceholder ?? t(I18N_KEYS.GENERAL.SELECT_AN_OPTION);

  // Two FPL labeling modes: an `id` means the caller owns a visible `<label htmlFor={id}>`, so we use
  // the manually-labeled trigger; otherwise we render the high-level trigger with a hidden label so
  // there is always an accessible name (see the `id` prop docs).
  const trigger =
    id != null ? (
      <Select.ManuallyLabeledTrigger
        id={id}
        placeholder={stringPlaceholder}
        width="fill"
        size={size}
        disabled={disabled}
      >
        {triggerChildren}
      </Select.ManuallyLabeledTrigger>
    ) : (
      <Select.Trigger
        label={<HiddenLabel>{accessibleLabel}</HiddenLabel>}
        placeholder={stringPlaceholder}
        width="fill"
        size={size}
        disabled={disabled}
      >
        {triggerChildren}
      </Select.Trigger>
    );

  return (
    <div className="nodrag nopan" style={{ width }}>
      <Select.Root
        value={selectedKey}
        onChange={handleChange}
        onOpenChange={handleOpenChange}
        onSelectionChange={handleSelectionChange}
        position={position}
      >
        {trigger}
        <Select.Container>
          {options.length === 0 ? (
            <Box
              component="li"
              role="presentation"
              sx={{ display: 'flex', justifyContent: 'center', listStyle: 'none' }}
            >
              <Typography variant="body-sm-rg" color={token.textSecondary}>
                {noOptionsText || t(I18N_KEYS.COMMON_COMPONENTS.BASE_SELECT.NO_OPTIONS_TEXT)}
              </Typography>
            </Box>
          ) : (
            options.map((option, index) => (
              <Fragment key={getOptionKey(option)}>
                {option.dividerBefore && index > 0 ? <Select.Separator /> : null}
                <Select.Option value={getOptionKey(option)}>{option.label}</Select.Option>
              </Fragment>
            ))
          )}
        </Select.Container>
      </Select.Root>
    </div>
  );
};
